import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/joy";
import { getGlobalPositions } from "../../../apiconfig/api";

interface PositionSelectorProps {
  selectedPositions: string[];
  onSelectPosition: (positions: string[]) => void;
}

interface GlobalPosition {
  id: string;
  displayName: string;
}

const PositionSelector: React.FC<PositionSelectorProps> = ({
  selectedPositions,
  onSelectPosition,
}) => {
  const [positions, setPositions] = useState<GlobalPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch global positions on component mount
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getGlobalPositions();
        setPositions(response.data?.globalPositions || []);
      } catch (err: any) {
        console.error('Error fetching global positions:', err);
        setError('Failed to load positions');
      } finally {
        setLoading(false);
      }
    };

    fetchPositions();
  }, []);

  const handlePositionClick = (positionId: string) => {
    const alreadySelected = selectedPositions.includes(positionId);

    const newPositions = alreadySelected
      ? selectedPositions.filter((p) => p !== positionId)
      : [...selectedPositions, positionId];

    onSelectPosition(newPositions);
  };

  const isSelected = (positionId: string) => {
    return selectedPositions.includes(positionId);
  };

  if (loading) {
    return (
      <Box>
        <Typography level="label" marginBottom="8px">
          Posts
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography level="label" marginBottom="8px">
          Posts
        </Typography>
        <Typography level="body-sm" color="danger">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography level="label" marginBottom="8px">
        Roles
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {positions.map((position) => (
          <Button
            key={position.id}
            onClick={() => handlePositionClick(position.id)}
            variant={isSelected(position.id) ? "solid" : "outlined"}
            sx={{
              borderRadius: "12px",
              textTransform: "capitalize",
              minWidth: "80px",
              height: "40px",
              bgcolor: isSelected(position.id)
                ? "#2E2D7E"
                : `${
                    window.location.pathname === "/admin/applicants"
                      ? "#F4F4F4"
                      : "#FFFFFF"
                  }`,
              color: isSelected(position.id) ? "#FFFFFF" : "#1A1B1D",
            }}
          >
            {position.displayName}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default PositionSelector;
